/**
 * SingleDutyRadioCard — supervisor car radios (95/97/99) and
 * Management / Engineering / Security Center radios.
 *
 * Single indicator:
 *   • ON  (blue) → on duty
 *   • OFF (gray) → off duty
 *
 * readOnly=true  → auto-detect only from personnel roster (95/97/99)
 * readOnly=false → auto + manual click override (engineering/management)
 */
import { Card, StatusDot } from '../ui'
import { WALKIE_ICON } from '../../assets'
import { useDesktopStore } from '../../stores/desktop'
import { markDirty } from '../../stores/sync'
import type { RadioSlot } from '../../stores/desktop'

interface SingleDutyRadioCardProps {
  radio: RadioSlot
  onUpdate: (patch: Partial<RadioSlot>) => void
  /** When true, duty state is auto-only — no manual click override */
  readOnly?: boolean
}

function normCS(cs: string): string {
  if (!cs) return ''
  const m = cs.match(/^([A-Za-z]+-?)(\d+)$/)
  return m ? m[1] + parseInt(m[2], 10) : cs
}

export default function SingleDutyRadioCard({ radio, onUpdate, readOnly }: SingleDutyRadioCardProps) {
  const personnel = useDesktopStore(s => s.personnel)
  const isOos = !!radio.oos

  const autoOnDuty =
    !!radio.assignedTo &&
    personnel.some(
      p =>
        p.name &&
        p.callSign &&
        normCS(p.callSign.toUpperCase()) === normCS(radio.assignedTo.toUpperCase()),
    )

  // readOnly: auto only. Otherwise manual override wins if set.
  const isOnDuty = isOos
    ? false
    : readOnly
      ? autoOnDuty
      : radio.manualOnDuty !== undefined
        ? radio.manualOnDuty
        : autoOnDuty

  const borderColor = isOos ? '#fca5a5' : isOnDuty ? '#60a5fa' : '#d1d5db'
  const imgOpacity  = isOnDuty ? 1 : 0.45
  const imgFilter   = isOnDuty ? 'none' : 'grayscale(100%)'

  function handleToggle() {
    if (isOos || readOnly) return
    markDirty()
    onUpdate({ manualOnDuty: !isOnDuty, available: isOnDuty })
  }

  return (
    <Card variant="item" borderColor={borderColor}>
      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 4, textAlign: 'center' }}>
        Radio {radio.id}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <div style={{ flexShrink: 0, width: 60, height: 60 }}>
          <img
            src={WALKIE_ICON}
            alt={`Radio ${radio.id}`}
            style={{ width: '100%', height: '100%', objectFit: 'contain', opacity: imgOpacity, filter: imgFilter }}
          />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4, flex: 1, minWidth: 0 }}>
          {isOos ? (
            <span style={{ fontSize: 11, fontWeight: 700, color: '#dc2626' }}>OOS</span>
          ) : (
            <>
              {radio.assignedTo && (
                <span style={{ fontSize: 11, fontWeight: 700, color: isOnDuty ? '#1d4ed8' : '#9ca3af' }}>
                  {radio.assignedTo}
                </span>
              )}

              <div
                onClick={handleToggle}
                title={readOnly ? undefined : isOnDuty ? 'Click to set Off Duty' : 'Click to set On Duty'}
                style={{
                  display: 'flex', alignItems: 'center', gap: 4,
                  cursor: readOnly ? 'default' : 'pointer',
                  userSelect: 'none',
                }}
              >
                <StatusDot variant={isOnDuty ? 'blue' : 'gray'} active={isOnDuty} />
                <span style={{ fontSize: 11, fontWeight: 700, color: isOnDuty ? '#1d4ed8' : '#6b7280' }}>
                  {isOnDuty ? 'On Duty' : 'Off Duty'}
                </span>
              </div>
            </>
          )}
        </div>
      </div>
    </Card>
  )
}
